import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ArrowLeft, ShieldAlert, Search } from 'lucide-react';
import { toast } from 'sonner';
import { employeeLicensesAPI } from '../lib/api';

const WINDOWS = [
  { value: '7', label: 'Next 7 days' },
  { value: '14', label: 'Next 14 days' },
  { value: '30', label: 'Next 30 days' },
  { value: '60', label: 'Next 60 days' },
  { value: '90', label: 'Next 90 days' },
];

export default function LicenseExpirationsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('30');
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const load = async (d) => {
    setLoading(true);
    try {
      const r = await employeeLicensesAPI.upcoming(d);
      setRows(r.data || []);
    } catch (e) {
      toast.error('Failed to load expirations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days]);

  const filtered = useMemo(() => rows.filter(r => {
    if (typeFilter !== 'all' && r.license_type !== typeFilter) return false;
    if (query) {
      const q = query.toLowerCase();
      const hay = `${r.employee_name} ${r.employee_number || ''} ${r.state || ''} ${r.license_number || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }), [rows, query, typeFilter]);

  const counts = useMemo(() => ({
    expired: rows.filter(r => r.days_until < 0).length,
    urgent: rows.filter(r => r.days_until >= 0 && r.days_until <= 7).length,
    soon: rows.filter(r => r.days_until > 7 && r.days_until <= 30).length,
    later: rows.filter(r => r.days_until > 30).length,
  }), [rows]);

  const colorFor = (d) => {
    if (d < 0) return 'text-red-700 bg-red-50 border-red-300';
    if (d <= 7) return 'text-red-700 bg-red-50 border-red-300';
    if (d <= 30) return 'text-amber-700 bg-amber-50 border-amber-300';
    return 'text-slate-700 bg-slate-50 border-slate-200';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-dashboard"><ArrowLeft className="w-4 h-4 mr-1" /> Dashboard</Button>
        <h1 className="page-title flex items-center gap-2"><ShieldAlert className="w-6 h-6 text-amber-600" /> License Expirations</h1>
      </div>

      {/* Top-line counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardHeader className="pb-2"><CardDescription>Expired</CardDescription><CardTitle className="text-3xl text-red-700">{counts.expired}</CardTitle></CardHeader></Card>
        <Card><CardHeader className="pb-2"><CardDescription>Within 7 days</CardDescription><CardTitle className="text-3xl text-red-600">{counts.urgent}</CardTitle></CardHeader></Card>
        <Card><CardHeader className="pb-2"><CardDescription>8–30 days</CardDescription><CardTitle className="text-3xl text-amber-600">{counts.soon}</CardTitle></CardHeader></Card>
        <Card><CardHeader className="pb-2"><CardDescription>31+ days</CardDescription><CardTitle className="text-3xl text-slate-700">{counts.later}</CardTitle></CardHeader></Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <CardTitle>Upcoming &amp; expired licenses</CardTitle>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
                <Input
                  placeholder="Search name, number, state…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="pl-8 w-56"
                  data-testid="license-search"
                />
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-44" data-testid="license-type-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="Armed">Armed</SelectItem>
                  <SelectItem value="Unarmed">Unarmed</SelectItem>
                  <SelectItem value="PI">PI</SelectItem>
                  <SelectItem value="Driver&apos;s License">Driver&apos;s License</SelectItem>
                  <SelectItem value="State Issued ID">State Issued ID</SelectItem>
                </SelectContent>
              </Select>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger className="w-40" data-testid="license-window"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WINDOWS.map(w => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && <p className="text-sm text-slate-500">Loading…</p>}
          {!loading && filtered.length === 0 && (
            <div className="py-10 text-center text-slate-500" data-testid="license-empty">
              <ShieldAlert className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p>No licenses match the current filters.</p>
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>License #</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Expiration</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(r => (
                  <TableRow
                    key={r.license_id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/employees/${r.employee_id}`)}
                    data-testid={`license-row-${r.license_id}`}
                  >
                    <TableCell>
                      <div className="font-medium">{r.employee_name || '—'}</div>
                      {r.employee_number && <div className="text-xs text-slate-500">#{r.employee_number}</div>}
                    </TableCell>
                    <TableCell className="font-mono">{r.state}</TableCell>
                    <TableCell className="font-mono">{r.license_number}</TableCell>
                    <TableCell><Badge variant="outline">{r.license_type}</Badge></TableCell>
                    <TableCell>{r.expiration_date}</TableCell>
                    <TableCell>
                      <span className={`inline-block px-2 py-0.5 rounded text-xs border ${colorFor(r.days_until)}`}>
                        {r.days_until < 0 ? `${Math.abs(r.days_until)}d ago` : `${r.days_until}d`}
                      </span>
                      {r.is_primary && <Badge className="ml-2 bg-amber-100 text-amber-800 hover:bg-amber-100">Primary</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
